import type { IncomingHttpHeaders } from 'http';

import { AUTH_COOKIE_KEY, EMPTY_STR, SEMI_COLON } from '@/utils/constants/common';

const COOKIE_REGEX = '=([^;]+);*';
type ExtractCookie = {
    cbscom?: string;
    maxAge?: number;
};

const extractAuthCookieFromResponse = (responseCookie: Array<string>): ExtractCookie => {
    if (!responseCookie) throw new Error('Failed to compile cookies');

    const cbscomRawCookie = responseCookie.find((item) => {
        return item.indexOf(AUTH_COOKIE_KEY) >= 0;
    });

    if (!cbscomRawCookie) throw new Error('Failed to compile cookies');

    const cbscom = cbscomRawCookie.match(`${AUTH_COOKIE_KEY}${COOKIE_REGEX}`)?.[1];
    const maxAge = Number(cbscomRawCookie.match(`Max-Age${COOKIE_REGEX}`)?.[1]);

    return {
        cbscom,
        maxAge,
    };
};

const extractHttpCookie = (cookies: string = EMPTY_STR, cookieName: string) => {
    if (!cookies) throw new Error('No cookies passed in');

    const splitCookies = cookies.split(SEMI_COLON);
    const rawCookie = splitCookies.find((item) => {
        return item.indexOf(cookieName) >= 0;
    });

    if (!rawCookie) return false;

    const cookieValue = rawCookie.match(`${cookieName}${COOKIE_REGEX}`)?.[1];

    return cookieValue;
};

const CMSOverridesCookieStringToObject = (input: string) => {
    const paramsObj: Record<string, string> = {};
    (input || '').split('!').forEach((override) => {
        if (!override.includes('~')) {
            return;
        }

        const [key, value] = override.split('~');
        paramsObj[key] = value;
    });

    return paramsObj;
};

/**
 * Parses the Cookie header string into a Map of name-value pairs
 * @param cookieHeader - The Cookie header string (e.g., "name1=value1; name2=value2")
 * @returns Map of cookie names to values
 */
const getCookiesMap = (
    cookieHeader: IncomingHttpHeaders['cookie'] | undefined,
): Map<string, string> => {
    const requestCookieMap = new Map<string, string>();

    if (!cookieHeader) {
        return requestCookieMap;
    }

    cookieHeader.split(';').forEach((cookie) => {
        const trimmedCookie = cookie.trim();
        const separatorIndex = trimmedCookie.indexOf('=');

        if (separatorIndex > 0) {
            const name = trimmedCookie.substring(0, separatorIndex).trim();
            const value = trimmedCookie.substring(separatorIndex + 1);
            requestCookieMap.set(name, value);
        }
    });

    return requestCookieMap;
};

export {
    CMSOverridesCookieStringToObject,
    extractAuthCookieFromResponse,
    extractHttpCookie,
    getCookiesMap,
};
